// Estado em memória do backend falso para Workflow (M1 §8 "Before the contract exists"): tarefas, exceções e
// aprovações semeadas em PILOT/SECOND e na rede. Funções puras (tomam o estado e devolvem um novo item ou lista),
// fáceis de testar sem HTTP. Nada aqui é fonte de verdade (o motor determinístico real fica no backend, regra 1).
import type { MeResponse } from '@giyus/api-client';

import { FIXTURE_IDS } from '../auth/me.fixture';
import type {
  ApprovalStatus,
  ExceptionSeverity,
  ExceptionStatus,
  TaskStatus,
  WorkflowApprovalPolicy,
  WorkflowConsequence,
  WorkflowEscalationRule,
  WorkflowOwnerRef,
  WorkflowScopeRef,
  WorkflowSourceRef,
  WorkPriority,
} from '../../modules/workflow/api/contract.preview';

let counter = 0;
/** Id determinístico e legível (a ordem de criação nos testes é estável). */
function nextId(prefix: string): string {
  counter += 1;
  return `fake-${prefix}-${String(counter).padStart(4, '0')}`;
}

// Espelham os tipos `readonly` do contrato preview, mas mutáveis: são o estado interno da loja, nunca a
// resposta HTTP (a serialização em `workflowRoutes.ts` os encaixa estruturalmente nos DTOs `readonly`).
export interface FakeChecklistItem {
  id: string;
  order: number;
  key: string | null;
  label: string | null;
  isRequired: boolean;
  isChecked: boolean;
  checkedAt: string | null;
  checkedBy: string | null;
}

export interface FakeComment {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  deletedAt: string | null;
  deleteReason: string | null;
}

export interface FakeHistoryEntry {
  id: string;
  action: string;
  actorName: string;
  actorRole: string;
  occurredAt: string;
  reason: string | null;
  summary: string | null;
}

export interface FakeTask {
  id: string;
  tenantId: string | null;
  studySiteId: string | null;
  typeCode: string;
  subject: string;
  description: string | null;
  priority: WorkPriority;
  status: TaskStatus;
  blockedReason: string | null;
  ownerKind: 'User' | 'Role';
  ownerUserId: string | null;
  ownerRoleCode: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  dueAt: string | null;
  createdAt: string;
  requiresEvidence: boolean;
  evidencePresent: boolean;
  checklist: FakeChecklistItem[];
  comments: FakeComment[];
  history: FakeHistoryEntry[];
  source: WorkflowSourceRef | null;
}

export interface FakeException {
  id: string;
  tenantId: string | null;
  studySiteId: string | null;
  typeCode: string;
  severity: ExceptionSeverity;
  status: ExceptionStatus;
  ownerKind: 'User' | 'Role';
  ownerUserId: string | null;
  ownerRoleCode: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  consequence: WorkflowConsequence;
  detail: string | null;
  reference: WorkflowSourceRef | null;
  raisedAt: string;
  slaDueAt: string;
  occurrenceCount: number;
  lastOccurredAt: string;
  history: FakeHistoryEntry[];
}

export interface FakeApprovalDecisionRecord {
  id: string;
  step: number;
  deciderUserId: string;
  deciderName: string;
  decision: 'Approved' | 'Rejected' | 'Returned';
  meaning: string;
  comment: string | null;
  decidedAt: string;
}

export interface FakeApprovalStep {
  order: number;
  approverRoleCodes: readonly string[];
  approverScope: 'SameScope' | 'Network';
  requiredPermission: string;
}

export interface FakeApproval {
  id: string;
  tenantId: string | null;
  studySiteId: string | null;
  actType: string;
  objectType: string;
  objectId: string;
  objectVersion: string | null;
  objectSummary: string | null;
  objectContentHash: string;
  requestedByUserId: string;
  requestedAt: string;
  dueAt: string;
  currentStep: number;
  steps: FakeApprovalStep[];
  status: ApprovalStatus;
  requiresSignature: boolean;
  decisions: FakeApprovalDecisionRecord[];
  closedAt: string | null;
}

export interface FakeWorkflowState {
  tasks: FakeTask[];
  exceptions: FakeException[];
  approvals: FakeApproval[];
  policies: WorkflowApprovalPolicy[];
  rules: WorkflowEscalationRule[];
}

function hoursFromNow(hours: number): string {
  return new Date(Date.now() + hours * 3_600_000).toISOString();
}

/** Semeia o estado (Parte "Seeds (A)" do plano M1, subconjunto usado pela UI). Determinístico o bastante para
 * testes: as datas são relativas ao instante da carga da página (o fake não usa `IClock`, é só demonstração). */
export function seedWorkflowState(): FakeWorkflowState {
  const tasks: FakeTask[] = [
    {
      id: nextId('task'),
      tenantId: FIXTURE_IDS.tenantPilot,
      studySiteId: null,
      typeCode: 'SCHEDULE_DUE_VISIT',
      subject: 'Agendar visita de acompanhamento — participante P-0142',
      description: 'A janela de protocolo abre em 2 dias; nenhuma consulta está agendada ainda.',
      priority: 'Normal',
      status: 'Open',
      blockedReason: null,
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'STUDY_COORDINATOR',
      assigneeUserId: null,
      assigneeName: null,
      dueAt: hoursFromNow(48),
      createdAt: hoursFromNow(-24),
      requiresEvidence: false,
      evidencePresent: false,
      checklist: [],
      comments: [],
      history: [
        {
          id: nextId('hist'),
          action: 'WF.TASK_CREATED',
          actorName: 'Sistema',
          actorRole: 'STUDY_COORDINATOR',
          occurredAt: hoursFromNow(-24),
          reason: null,
          summary: 'Tarefa criada pelo evento de agendamento.',
        },
      ],
      source: {
        entityType: 'Study',
        entityId: '01926f3e-0000-7000-8000-0000000000s1',
        label: 'Estudo-âncora',
      },
    },
    {
      id: nextId('task'),
      tenantId: FIXTURE_IDS.tenantPilot,
      studySiteId: FIXTURE_IDS.sitePilotSede,
      typeCode: 'CONTACT_PARTICIPANT_UNREACHABLE',
      subject: 'Contatar participante inalcançável — 3 tentativas sem resposta',
      description: 'Ligações e mensagens sem retorno desde a última tentativa de contato.',
      priority: 'High',
      status: 'Open',
      blockedReason: null,
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'STUDY_COORDINATOR',
      assigneeUserId: null,
      assigneeName: null,
      dueAt: hoursFromNow(-6),
      createdAt: hoursFromNow(-72),
      requiresEvidence: true,
      evidencePresent: false,
      checklist: [
        {
          id: nextId('chk'),
          order: 1,
          key: 'called',
          label: null,
          isRequired: true,
          isChecked: false,
          checkedAt: null,
          checkedBy: null,
        },
        {
          id: nextId('chk'),
          order: 2,
          key: 'messaged',
          label: null,
          isRequired: false,
          isChecked: false,
          checkedAt: null,
          checkedBy: null,
        },
      ],
      comments: [],
      history: [
        {
          id: nextId('hist'),
          action: 'WF.TASK_CREATED',
          actorName: 'Sistema',
          actorRole: 'STUDY_COORDINATOR',
          occurredAt: hoursFromNow(-72),
          reason: null,
          summary: null,
        },
      ],
      source: null,
    },
    {
      id: nextId('task'),
      tenantId: FIXTURE_IDS.tenantSecond,
      studySiteId: null,
      typeCode: 'RENEW_DOCUMENT',
      subject: 'Renovar apólice de seguro do estudo',
      description: null,
      priority: 'Low',
      status: 'Open',
      blockedReason: null,
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'STUDY_COORDINATOR',
      assigneeUserId: null,
      assigneeName: null,
      dueAt: hoursFromNow(120),
      createdAt: hoursFromNow(-12),
      requiresEvidence: false,
      evidencePresent: false,
      checklist: [],
      comments: [],
      history: [],
      source: null,
    },
    {
      id: nextId('task'),
      tenantId: null,
      studySiteId: null,
      typeCode: 'CAPACITY_SHORTFALL',
      subject: 'Insuficiência de capacidade — sala PHLEBOTOMY no PILOT',
      description: 'Ocupação prevista acima de 95% para a próxima semana.',
      priority: 'Urgent',
      status: 'Open',
      blockedReason: null,
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'HOLDING_ADMIN',
      assigneeUserId: null,
      assigneeName: null,
      dueAt: hoursFromNow(-3),
      createdAt: hoursFromNow(-96),
      requiresEvidence: false,
      evidencePresent: false,
      checklist: [],
      comments: [],
      history: [],
      source: null,
    },
    {
      id: nextId('task'),
      tenantId: null,
      studySiteId: null,
      typeCode: 'REVIEW_PROCEDURE_CATALOG_CHANGE',
      subject: 'Revisar mudança no catálogo de procedimentos — SPIROMETRY v2',
      description: null,
      priority: 'Normal',
      status: 'Open',
      blockedReason: null,
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'STARTUP_SPECIALIST',
      assigneeUserId: null,
      assigneeName: null,
      dueAt: hoursFromNow(72),
      createdAt: hoursFromNow(-6),
      requiresEvidence: false,
      evidencePresent: false,
      checklist: [],
      comments: [],
      history: [],
      source: null,
    },
  ];

  const exceptions: FakeException[] = [
    {
      id: nextId('exc'),
      tenantId: FIXTURE_IDS.tenantPilot,
      studySiteId: FIXTURE_IDS.sitePilotSede,
      typeCode: 'APPOINTMENT_RESOURCE_CONFLICT',
      severity: 'High',
      status: 'Open',
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'STUDY_COORDINATOR',
      assigneeUserId: null,
      assigneeName: null,
      consequence: {
        messageKey: 'exception:consequences.appointmentResourceConflict',
        args: { resource: 'Sala VITALS' },
        detail: null,
      },
      detail: 'Duas consultas concorrentes reservaram o mesmo recurso após uma alteração manual.',
      reference: {
        entityType: 'Appointment',
        entityId: '01926f3e-0000-7000-8000-0000000000ap1',
        label: 'Consulta #482',
      },
      raisedAt: hoursFromNow(-30),
      slaDueAt: hoursFromNow(-2),
      occurrenceCount: 3,
      lastOccurredAt: hoursFromNow(-2),
      history: [],
    },
    {
      id: nextId('exc'),
      tenantId: null,
      studySiteId: null,
      typeCode: 'JOB_FAILED',
      severity: 'High',
      status: 'Open',
      ownerKind: 'Role',
      ownerUserId: null,
      ownerRoleCode: 'HOLDING_ADMIN',
      assigneeUserId: null,
      assigneeName: null,
      consequence: {
        messageKey: null,
        args: null,
        detail: 'EscalationScanFunction falhou 2 vezes seguidas.',
      },
      detail: null,
      reference: null,
      raisedAt: hoursFromNow(-4),
      slaDueAt: hoursFromNow(20),
      occurrenceCount: 2,
      lastOccurredAt: hoursFromNow(-4),
      history: [],
    },
  ];

  const approvals: FakeApproval[] = [
    {
      id: nextId('appr'),
      tenantId: null,
      studySiteId: null,
      actType: 'ntf.template-version.internal',
      objectType: 'MessageTemplate',
      objectId: 'task.assigned',
      objectVersion: 'v2',
      objectSummary: 'task.assigned v2 — ajuste de assunto e prazo em pt-BR/en-US',
      objectContentHash: 'sha256:seed-task-assigned-v2',
      requestedByUserId: 'fake-seed-author',
      requestedAt: hoursFromNow(-8),
      dueAt: hoursFromNow(160),
      currentStep: 1,
      // Simplificação do modo fake: a política real (M1 §2 seeds) usa QUALITY_LEAD; aqui o papel do
      // aprovador é HOLDING_ADMIN para caber nos cenários fixos de `/me` (Anexo 14.7) sem criar um sexto
      // cenário só para este fluxo de demonstração. TODO(GIY-025): alinhar quando o backend real existir.
      steps: [
        {
          order: 1,
          approverRoleCodes: ['HOLDING_ADMIN'],
          approverScope: 'Network',
          requiredPermission: 'notification.template.approve',
        },
      ],
      status: 'Pending',
      requiresSignature: true,
      decisions: [],
      closedAt: null,
    },
  ];

  const policies: WorkflowApprovalPolicy[] = [
    {
      actType: 'ntf.template-version.internal',
      ownerModule: 'NTF',
      steps: [
        {
          order: 1,
          approverRoleCodes: ['QUALITY_LEAD'],
          approverScope: 'Network',
          requiredPermission: 'notification.template.approve',
          distinctFromPreviousSteps: false,
        },
      ],
      dueDays: 7,
      requiresSignature: true,
      allowedMeanings: ['Approved'],
      requiresTwoKeys: false,
      status: 'Active',
    },
    {
      actType: 'ntf.template-version.participant',
      ownerModule: 'NTF',
      steps: [
        {
          order: 1,
          approverRoleCodes: ['DPO'],
          approverScope: 'Network',
          requiredPermission: 'notification.template.approve',
          distinctFromPreviousSteps: false,
        },
      ],
      dueDays: 7,
      requiresSignature: true,
      allowedMeanings: ['Approved'],
      requiresTwoKeys: false,
      status: 'Active',
    },
  ];

  const rules: WorkflowEscalationRule[] = [
    {
      code: 'EXC-CRITICAL-L1',
      targetKind: 'Exception',
      typeCode: null,
      minUrgency: 'Critical',
      level: 1,
      basis: 'SinceDue',
      afterHours: 0,
      action: 'FollowUpTask',
      targetRoleCode: 'HOLDING_ADMIN',
      targetScope: 'Network',
      status: 'Active',
    },
    {
      code: 'EXC-HIGH-L1',
      targetKind: 'Exception',
      typeCode: null,
      minUrgency: 'High',
      level: 1,
      basis: 'SinceDue',
      afterHours: 24,
      action: 'Reassign',
      targetRoleCode: 'TENANT_ADMIN',
      targetScope: 'SameTenant',
      status: 'Active',
    },
    {
      code: 'TASK-OVERDUE-L1',
      targetKind: 'Task',
      typeCode: null,
      minUrgency: null,
      level: 1,
      basis: 'SinceDue',
      afterHours: 24,
      action: 'FollowUpTask',
      targetRoleCode: 'TENANT_ADMIN',
      targetScope: 'SameTenant',
      status: 'Active',
    },
  ];

  return { tasks, exceptions, approvals, policies, rules };
}

export function scopeOf(item: {
  tenantId: string | null;
  studySiteId: string | null;
}): WorkflowScopeRef {
  if (item.tenantId === null) {
    return { level: 'Network', tenantId: null, studySiteId: null };
  }
  return {
    level: item.studySiteId ? 'Study' : 'Tenant',
    tenantId: item.tenantId,
    studySiteId: item.studySiteId,
  };
}

export function ownerOf(item: {
  ownerKind: 'User' | 'Role';
  ownerUserId: string | null;
  ownerRoleCode: string | null;
}): WorkflowOwnerRef {
  return { kind: item.ownerKind, userId: item.ownerUserId, roleCode: item.ownerRoleCode };
}

/** Papéis efetivos do chamador no escopo do item (rede quando `tenantId` é `null`, senão o tenant). */
export function callerRolesInScope(me: MeResponse, tenantId: string | null): readonly string[] {
  if (tenantId === null) {
    return me.networkAccess.roles;
  }
  const context = me.contexts.find((c) => c.tenantId === tenantId);
  return context ? context.effectiveRoles.map((r) => r.role) : [];
}

export function callerHasRole(me: MeResponse, roleCode: string, tenantId: string | null): boolean {
  return callerRolesInScope(me, tenantId).includes(roleCode);
}

/** Regra 12.2 (plano M1 §2, "Ownership"): dono do usuário, ou papel ainda não assumido, coberto pelo escopo. */
export function isTaskOrExceptionVisible(
  me: MeResponse,
  item: {
    ownerKind: 'User' | 'Role';
    ownerUserId: string | null;
    ownerRoleCode: string | null;
    assigneeUserId: string | null;
    tenantId: string | null;
  },
): boolean {
  if (item.assigneeUserId === me.user.id || item.ownerUserId === me.user.id) {
    return true;
  }
  if (item.assigneeUserId !== null) {
    return false;
  }
  return item.ownerRoleCode !== null && callerHasRole(me, item.ownerRoleCode, item.tenantId);
}

export function isApprovalVisible(me: MeResponse, approval: FakeApproval): boolean {
  const step = approval.steps.find((s) => s.order === approval.currentStep);
  if (!step) {
    return false;
  }
  return step.approverRoleCodes.some((role) => callerHasRole(me, role, approval.tenantId));
}

export function recordHistory(
  entries: FakeHistoryEntry[],
  action: string,
  me: MeResponse,
  reason: string | null = null,
  summary: string | null = null,
): void {
  entries.push({
    id: nextId('hist'),
    action,
    actorName: me.user.displayName,
    actorRole: me.networkAccess.hasAccess
      ? (me.networkAccess.roles[0] ?? '—')
      : (me.contexts[0]?.effectiveRoles[0]?.role ?? '—'),
    occurredAt: new Date().toISOString(),
    reason,
    summary,
  });
}

export { nextId };
